import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import { AlarmClock, CircleCheck, Play, ThumbsDown, ThumbsUp } from "lucide-mui";
import { useState } from "react";
import BulkActionBar from "../BulkActionBar.tsx";
import ReviewDecisionDialog from "./ReviewDecisionDialog.tsx";
import SnoozeMenu from "./SnoozeMenu.tsx";
import { canMarkDone, canReview, onWorklist } from "./rules.ts";
import type { ReviewCopy, ReviewDecision, ReviewSubject, UseReview } from "./types.ts";

/** The bar under the table while rows are selected. */
export default function ReviewBulkBar({
  selected,
  onClear,
  useReview,
  copy,
  canContinue = true,
}: {
  selected: ReviewSubject[];
  onClear: () => void;
  useReview: UseReview;
  copy: ReviewCopy;
  // Continuing parked steps may need a scope of its own.
  canContinue?: boolean;
}) {
  const { continueSteps, snooze, done, busy } = useReview(onClear);
  const [decision, setDecision] = useState<ReviewDecision | null>(null);
  if (selected.length === 0) return null;
  const ids = selected.map((s) => s.id);
  const waiting = selected.filter((s) => s.awaiting_approval).length;
  const reviewable = selected.filter(canReview).length;
  // Done only means something on the worklist; the rest of the selection is left as it is.
  const finishable = selected.filter((s) => canMarkDone(s) && !s.done_at).map((s) => s.id);
  const partly = finishable.length > 0 && finishable.length < selected.length;
  // Snooze, too, applies to the worklist's rows only.
  const snoozable = selected.filter(onWorklist).map((s) => s.id);
  return (
    <BulkActionBar count={selected.length} onClear={onClear} sx={{ mt: 1 }}>
      {canContinue && (
        <Tooltip title={waiting ? `${waiting} waiting to continue` : "None of these is waiting"}>
          <span>
            <Button
              size="small"
              variant="contained"
              color="warning"
              startIcon={<Play />}
              disabled={busy || waiting === 0}
              onClick={() => continueSteps.mutate(ids)}
            >
              Continue{waiting ? ` (${waiting})` : ""}
            </Button>
          </span>
        </Tooltip>
      )}
      {/* Pursue & snooze sits on Pursue, like the drawer's alarm beside its thumbs up. */}
      <Tooltip title={reviewable ? "" : copy.notReviewable}>
        <span>
          <ButtonGroup
            size="small"
            variant="contained"
            color="success"
            disabled={busy || reviewable === 0}
          >
            <Button
              startIcon={<ThumbsUp />}
              onClick={() => setDecision({ ids, action: "accept" })}
            >
              Pursue
            </Button>
            <Tooltip
              title={busy || !reviewable ? "" : "Pursue and put aside on the worklist until a day"}
            >
              <Button
                aria-label="Pursue & snooze"
                onClick={() => setDecision({ ids, action: "accept", snooze: true })}
              >
                <AlarmClock fontSize="small" />
              </Button>
            </Tooltip>
          </ButtonGroup>
        </span>
      </Tooltip>
      <Tooltip title={finishable.length ? "" : copy.notOnWorklist}>
        <span>
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={<CircleCheck />}
            disabled={busy || finishable.length === 0}
            onClick={() => done.mutate({ ids: finishable, done: true })}
          >
            Done{partly ? ` (${finishable.length})` : ""}
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={reviewable ? "" : copy.notReviewable}>
        <span>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<ThumbsDown />}
            disabled={busy || reviewable === 0}
            onClick={() => setDecision({ ids, action: "disqualify" })}
          >
            Disqualify
          </Button>
        </span>
      </Tooltip>
      {snoozable.length > 0 && (
        <SnoozeMenu
          disabled={busy}
          onSnooze={(until) => snooze.mutate({ ids: until ? snoozable : ids, until })}
        />
      )}
      <ReviewDecisionDialog
        decision={decision}
        onClose={() => setDecision(null)}
        onDone={onClear}
        useReview={useReview}
        copy={copy}
      />
    </BulkActionBar>
  );
}
